import * as React from 'react';
import {EventMatchLabel} from 'Src/components/EventMatchLabel/EventMatchLabel';
import { SchoolEvent } from '../../../../models/event';
import moment from 'moment';
import { getEventGeneratedName } from '../../../../helpers/accessor/accessor';
import { getSignEventType, getEventResultForCalendar } from '../../../../helpers/calendar/calendar';
import { getCalendarEventVisualStatus } from '../../../../helpers/calendar/eventVisualStatus';
import { AppUser } from 'Src/views/App/App';

interface Props {
  user: AppUser;
  events: SchoolEvent[];
  onEventClick: (eventId: string) => void;
}

const TYPE_DETAILS = {
  F: { short: 'Fixture', className: 'fixture', icon: 'fixture' },
  C: { short: 'Club', className: 'club', icon: 'club' },
  T: { short: 'Tournament event', className: 'tournamentEvent', icon: 'trophy' }
};

function renderCalendarListIcon(icon: string) {
  switch (icon) {
    case 'club':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3 5 7v10l7 4 7-4V7l-7-4z" />
          <path d="M5 7l7 4 7-4" />
          <path d="M12 11v10" />
        </svg>
      );

    case 'trophy':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 4h8v3a4 4 0 0 1-8 0V4z" />
          <path d="M8 5H5a2 2 0 0 0 2 4h1" />
          <path d="M16 5h3a2 2 0 0 1-2 4h-1" />
          <path d="M12 11v4" />
          <path d="M9 20h6" />
        </svg>
      );

    default:
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 3v3M16 3v3M4 9h16" />
          <rect x="4" y="5" width="16" height="15" rx="3" />
        </svg>
      );
  }
}

export function EventAndTournamentsTable(props: Props) {
  const { events, user, onEventClick } = props;
  const { activeSchoolId } = user;

  if (events.length === 0) {
    return (
      <div className="cal-empty">
        <div className="cal-empty__icon">{renderCalendarListIcon('fixture')}</div>
        <div className="cal-empty__title">Nothing scheduled</div>
        <div className="cal-empty__text">No events on this day for the selected filter.</div>
      </div>
    );
  }

  return (
    <div className="cal-events">
      {events.map((event, index) => {
        const sign = getSignEventType(event);
        const type = TYPE_DETAILS[sign] || TYPE_DETAILS.F;
        const result = getEventResultForCalendar(event, activeSchoolId);
        const visualStatus = getCalendarEventVisualStatus(event, activeSchoolId);

        return (
          <button
            type="button"
            onClick={() => onEventClick(event.id)}
            key={`event_${index}`}
            className={`cal-ev cal-ev--${type.className} ${visualStatus.eventClassName}`}
          >
            <span className="cal-ev__time">{moment(event.startTime).format('HH:mm')}</span>
            <span className="cal-ev__bar" aria-hidden="true" />
            <span className="cal-ev__ico" aria-hidden="true">{renderCalendarListIcon(type.icon)}</span>
            <span className="cal-ev__main">
              <span className="cal-ev__name">{getEventGeneratedName(event, { user: user })}</span>
              <EventMatchLabel event={event} />
              <span className="cal-ev__meta">
                {type.short}{result && result !== '-' ? ` · ${result}` : ''}
              </span>
            </span>
            <span className="cal-ev__right">
              <span className={`cal-badge ${visualStatus.badgeClassName}`}>{visualStatus.label}</span>
              <span className="cal-ev__chev" aria-hidden="true">›</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
